/* eslint-disable import/no-unused-modules */
import { ApolloProvider } from '@apollo/client';
import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
} from 'react-router-dom';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/es/integration/react';
import DefaultNamespaceFormatsProvider from '../editor/DefaultNamespaceFormatsProvider';
import { AllGroupsProvider } from '../groups/useAllGroups';
import { AllSlimPostsProvider } from '../post/useAllSlimPosts';
import { SearchBarContextProvider } from '../topbar/SearchBarContext';
import { AllTopicsProvider } from '../topic/useAllTopics';
import { AllUsersProvider } from '../user/useAllUsers';
import getApolloClient from './apollo/getApolloClient';
import appRoutes from './appRoutes';
import getReduxStore from './redux/getReduxStore';
import ErrorBoundary from '../components/ErrorBoundary';
import RootErrorBoundaryFallback from './RootErrorBoundaryFallback';

const app = async () => {
  const apolloClient = await getApolloClient();
  const store = getReduxStore();

  const persistor = persistStore(store);

  const router = createBrowserRouter(createRoutesFromElements(appRoutes));

  const root = createRoot(document.getElementById('root') as HTMLElement);
  root.render(
    <ErrorBoundary
      name="root"
      renderFallback={({ isCausedByTranslation }) => (
        <RootErrorBoundaryFallback
          isCausedByTranslation={isCausedByTranslation}
        />
      )}
    >
      <DefaultNamespaceFormatsProvider>
        <Provider store={store}>
          <ApolloProvider client={apolloClient}>
            <AllUsersProvider>
              <AllGroupsProvider>
                <AllTopicsProvider>
                  <AllSlimPostsProvider>
                    <PersistGate loading={null} persistor={persistor}>
                      <HelmetProvider>
                        <DndProvider backend={HTML5Backend}>
                          <SearchBarContextProvider>
                            <RouterProvider router={router} />
                          </SearchBarContextProvider>
                        </DndProvider>
                      </HelmetProvider>
                    </PersistGate>
                  </AllSlimPostsProvider>
                </AllTopicsProvider>
              </AllGroupsProvider>
            </AllUsersProvider>
          </ApolloProvider>
        </Provider>
      </DefaultNamespaceFormatsProvider>
    </ErrorBoundary>,
  );
};

export default app;
